//#region IMPORTS
import {
  BuildingStorefrontIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { fromBackendDate } from "../../helpers/fromBackendDate";
import { useTranslation } from "react-i18next";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  template: InstoreTemplate;
  isAdmin: boolean;
  hqTeam?: boolean;
  viewHandler?: () => void;
  editHandler?: () => void;
  orderHandler?: () => void;
}
//#endregion

export default function PromosPageStaticItem({
  template,
  isAdmin,
  hqTeam = false,
  viewHandler,
  editHandler,
  orderHandler,
}: Props) {
  //#region SETUP
  const { t } = useTranslation();
  const fromDate = fromBackendDate(template.from);
  const untilDate = fromBackendDate(template.until);

  // Main action by role — admin views the template, everyone else (HQ
  // included) orders.
  const mainHandler = isAdmin ? viewHandler : orderHandler;
  //#endregion

  //#region RENDER
  return (
    <li className="relative flex items-center justify-between gap-x-6 py-5 text-base">
      <div className="flex min-w-0 gap-x-4">
        <div
          className="h-16 w-16 flex-none rounded-full flex items-center justify-center 2xl:h-32 2xl:w-32 border-2"
          style={{ backgroundColor: "#9eab80", borderColor: "#3e5902" }}
        >
          <BuildingStorefrontIcon
            className="h-6 w-6 2xl:h-12 2xl:w-12"
            style={{ color: "#3e5902" }}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0 flex flex-col justify-center">
          <button
            type="button"
            onClick={mainHandler}
            className="text-left text-xl font-semibold leading-6 text-gray-900 hover:text-pink-700"
          >
            Week orders
          </button>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-x-4">
        <div className="hidden sm:flex sm:flex-col sm:items-end">
          {isAdmin && (
            <p className="leading-6 text-gray-900">{template.name}</p>
          )}
          <div className="mt-1 flex items-center gap-x-1.5">
            <div className="rounded-full bg-emerald-500/20 p-1">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </div>
            <p className="text-gray-500 text-xs xl:text-base">
              {t("rituals.thisProjectIsAvailableFrom$VAR$until$VAR2$")
                .replace("$VAR$", fromDate)
                .replace("$VAR2$", untilDate)}
            </p>
          </div>
        </div>

        {isAdmin ? (
          <Menu as="div" className="relative flex-none">
            <Menu.Button className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-900">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={viewHandler}
                      className={classNames(
                        active ? "bg-gray-50" : "",
                        "block w-full px-3 py-1 text-sm font-bold leading-6 text-sky-900",
                      )}
                    >
                      view
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={editHandler}
                      className={classNames(
                        active ? "bg-gray-50" : "",
                        "block w-full px-3 py-1 text-sm leading-6 text-amber-600",
                      )}
                    >
                      edit
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        ) : (
          <button
            onClick={orderHandler}
            type="button"
            className="ml-4 rounded-md bg-pink-700 px-2.5 py-1.5 text-xs xl:text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-700"
          >
            {t("rituals.order").toLocaleUpperCase()}
          </button>
        )}
      </div>
    </li>
  );
  //#endregion
}
